/**
 * Transform cluster border (original coords, y from bottom) → SVG pixel coords
 * border: [[x, y], ...] where y=0 is bottom
 * returns: [[svgX, svgY], ...] where svgY=0 is top
 */
export const borderToSvgPoints = (border, imageH, scale) => border.map(([x, y]) => [x * scale, (imageH - y) * scale])

/**
 * Transform SVG pixel coords back to cluster border format
 */
export const svgPointsToBorder = (svgPoints, imageH, scale) =>
  svgPoints.map(([x, y]) => [x / scale, imageH - y / scale])

/**
 * Ray-casting algorithm: is point inside polygon?
 * polygon and point must use the same coordinate space
 */
export const isPointInsidePolygon = (polygon, point) => {
  const n = polygon.length
  let inside = false
  let [x0, y0] = polygon[n - 1]
  const [px, py] = point

  for (let i = 0; i < n; i++) {
    const [x1, y1] = polygon[i]
    if (y1 > py !== y0 > py && px < ((x0 - x1) * (py - y1)) / (y0 - y1) + x1) {
      inside = !inside
    }
    x0 = x1
    y0 = y1
  }

  return inside
}

/**
 * Returns answer heatmap points that fall inside the given polygon.
 * polygonUnscaled: [[x, y], ...] in unscaled SVG coords (y from top)
 * answer.heatmap.points: [{ x, y, uid, comment?, weights? }, ...] where y is from bottom
 */
export const getPointsInsideSelection = (polygonUnscaled, answer, imageH) => {
  const allPoints = answer?.heatmap?.points || []

  return allPoints.reduce((result, point, index) => {
    // Convert point to same coordinate space as polygon (unscaled, y from top)
    if (isPointInsidePolygon(polygonUnscaled, [point.x, imageH - point.y])) {
      result.push({ ...point, index })
    }
    return result
  }, [])
}

/**
 * Compute the lasso data payload for saving.
 * svgPoints: confirmed polygon points in scaled SVG coords
 */
export const calcLassoData = (svgPoints, scale, imageH, answer) => {
  // Step 1: unscale (keep y-from-top for polygon math)
  const unscaledPoints = svgPoints.map(([x, y]) => [x / scale, y / scale])

  // Step 2: border = flip Y back to original (y from bottom)
  const border = unscaledPoints.map(([x, y]) => [x, imageH - y])

  // Step 3: find answer points inside selection
  const points = getPointsInsideSelection(unscaledPoints, answer, imageH)
  const pointsSelectionIndexes = points.map(p => p.index)

  return { border, points, pointsSelectionIndexes }
}

export const calcTooltipItems = (svgPoints, scale, imageH, answer) => {
  const unscaledPoints = svgPoints.map(([x, y]) => [x / scale, y / scale])
  const selected = getPointsInsideSelection(unscaledPoints, answer, imageH)

  const pins = selected.length
  if (!pins) return [{ value: null, label: 'No pins were selected' }]

  const uniqueUids = [...new Set(selected.map(p => p.uid))]
  const comments = selected.filter(p => p?.comment).length

  let respondents
  if (answer?.weights) {
    const [, weightSum] = selected.reduce(
      ([seen, s], p) => {
        if (!seen.includes(p.uid)) return [[...seen, p.uid], s + Number(p.weights)]
        return [seen, s]
      },
      [[], 0]
    )
    respondents = weightSum.toFixed(2)
  } else {
    respondents = uniqueUids.length
  }

  const pinLabel = pins !== 1 ? 'pins' : 'pin'
  const respondentLabel = respondents !== 1 ? 'respondents' : 'respondent'

  if (comments > 0) {
    const commentLabel = comments !== 1 ? 'comments' : 'comment'
    return [
      { value: pins, label: `${pinLabel} and` },
      { value: comments, label: `${commentLabel} from` },
      { value: respondents, label: respondentLabel }
    ]
  }

  return [
    { value: pins, label: `${pinLabel} from` },
    { value: respondents, label: respondentLabel }
  ]
}
